import { ScrollContext } from '@natera/platform/lib/components/scrollbar'
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { Document as ReactPDFDocument } from 'react-pdf'
import { cn } from '~/shared/lib/cn'
import { usePrevious } from '~/shared/lib/use-previous'
import { usePagination, useResizableContainer, useScaling } from '../model'
import { LoadingSkeleton } from './loading-skeleton'
import { MediaViewerControl } from './media-viewer-control'
import { PDFPage } from './pdf-page'

export interface PDFPaginationInfo {
  scrollableRef: RefObject<HTMLDivElement | null>
  currentPage: number
  totalPages: number
}

interface PDFContentProps {
  className?: string
  file: string | null
  startPage?: number
  endPage?: number
  isLoading?: boolean
  onDownload?: () => void
  onExpandContent?: () => void
  onMinimizeContent?: () => void
  onOpenInNewTab?: () => void
  onInteraction?: (type: string) => void
  initialFitToWidth?: boolean
  onPaginationReady?: (info: PDFPaginationInfo) => void
  rotation?: {
    getRotation: (pageNumber: number) => number
    rotateLeft: (pageNumber: number) => void
    rotateRight: (pageNumber: number) => void
  }
}

export function PDFContent({
  className,
  file,
  startPage = 1,
  endPage,
  isLoading,
  onDownload,
  onExpandContent,
  onMinimizeContent,
  onOpenInNewTab,
  onInteraction,
  initialFitToWidth = false,
  onPaginationReady,
  rotation,
}: PDFContentProps) {
  const { containerRef, containerWidth } = useResizableContainer()
  const {
    fitToWidth,
    scaleFactor,
    onZoomOut,
    onZoomIn,
    onFitToWidth,
    zoomOutDisabled,
    zoomInDisabled,
    scrollInfoRef,
  } = useScaling({ initialFitToWidth })

  const {
    initPagesOnDocumentLoad,
    scrollableRef,
    pagesToDisplay,
    pageText,
    handlePageTextChange,
    assignPageRef,
  } = usePagination({ startPage, endPage })

  const controlRef = useRef<HTMLDivElement>(null)
  const [naturalControlWidth, setNaturalControlWidth] = useState(0)

  // Track previous values to detect zoom changes
  const prevScaleFactor = usePrevious(scaleFactor)
  const prevFitToWidth = usePrevious(fitToWidth)

  // Restore scroll position after zoom change
  useEffect(() => {
    // Check if zoom changed
    const scaleChanged = prevScaleFactor !== undefined && prevScaleFactor !== scaleFactor
    const fitChanged = prevFitToWidth !== undefined && prevFitToWidth !== fitToWidth

    if (!scaleChanged && !fitChanged) return
    if (!scrollableRef.current) return

    const scrollInfo = scrollInfoRef.current
    if (!scrollInfo) return

    // Wait for DOM update
    requestAnimationFrame(() => {
      if (!scrollableRef.current) return

      const container = scrollableRef.current
      const newScrollHeight = container.scrollHeight
      const newScrollWidth = container.scrollWidth
      const clientHeight = container.clientHeight
      const clientWidth = container.clientWidth
      const maxScrollTop = newScrollHeight - clientHeight
      const maxScrollLeft = newScrollWidth - clientWidth

      // Restore center position for both dimensions
      const newVisibleCenterY = scrollInfo.ratioY * newScrollHeight
      const newVisibleCenterX = scrollInfo.ratioX * newScrollWidth
      let newScrollTop = newVisibleCenterY - clientHeight / 2
      let newScrollLeft = newVisibleCenterX - clientWidth / 2

      // Clamp to valid range
      newScrollTop = Math.max(0, Math.min(newScrollTop, maxScrollTop))
      newScrollLeft = Math.max(0, Math.min(newScrollLeft, maxScrollLeft))

      container.scrollTop = newScrollTop
      container.scrollLeft = newScrollLeft
    })
  }, [scaleFactor, fitToWidth, prevScaleFactor, prevFitToWidth, scrollableRef, scrollInfoRef])

  // Measure natural control width (child div, not scaled parent)
  useEffect(() => {
    if (controlRef.current && pagesToDisplay.length > 0) {
      // Measure the inner child div which has the actual content
      const innerDiv = controlRef.current.children[0] as HTMLDivElement
      if (innerDiv) {
        const width = innerDiv.offsetWidth
        setNaturalControlWidth(width)
      }
    }
  }, [pagesToDisplay.length])

  const dynamicScale = useMemo(() => {
    if (naturalControlWidth === 0) return 100 // Fallback to default scale
    const availableWidth = containerWidth - 40 // 20px margins on each side
    const calculatedScale = (availableWidth / naturalControlWidth) * 100
    return Math.min(100, calculatedScale)
  }, [naturalControlWidth, containerWidth])

  const currentPage = parseInt(pageText) || 1

  // Notify parent of pagination state for analytics hooks
  useEffect(() => {
    onPaginationReady?.({ scrollableRef, currentPage, totalPages: pagesToDisplay.length })
  }, [onPaginationReady, scrollableRef, currentPage, pagesToDisplay.length])

  const handleRotateLeft = rotation ? () => rotation.rotateLeft(currentPage) : undefined
  const handleRotateRight = rotation ? () => rotation.rotateRight(currentPage) : undefined

  const onPDFDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    initPagesOnDocumentLoad(numPages)
  }

  if (!file && !isLoading) {
    return <div className="flex h-full items-center justify-center">No PDF file available</div>
  }

  return (
    <div
      ref={containerRef}
      className={cn('group h-full overflow-hidden', className)}
    >
      <ScrollContext
        ref={scrollableRef}
        component="div"
        className="h-full"
      >
        <div className="bg-surface-medium-inverse text-surface-lowest h-full">
          {isLoading ? (
            <LoadingSkeleton
              variant="document"
              initialFitToWidth={initialFitToWidth}
            />
          ) : (
            <ReactPDFDocument
              className="bg-surface-medium-inverse"
              file={file}
              onLoadSuccess={onPDFDocumentLoadSuccess}
              loading={<LoadingSkeleton initialFitToWidth={initialFitToWidth} />}
            >
              {pagesToDisplay.map((_, index) => (
                <div
                  key={index}
                  ref={(el) => {
                    assignPageRef(el, index)
                  }}
                  className={cn('bg-surface-medium-inverse relative mx-auto w-fit pt-3 last:pb-3', {
                    'px-[66px]': !fitToWidth,
                    'px-3': fitToWidth,
                  })}
                >
                  <PDFPage
                    pageNumber={index + startPage}
                    containerWidth={containerWidth}
                    fitToWidth={fitToWidth}
                    scaleFactor={scaleFactor}
                    rotation={rotation?.getRotation(index + startPage)}
                  />
                </div>
              ))}
            </ReactPDFDocument>
          )}
        </div>
      </ScrollContext>

      <MediaViewerControl
        ref={controlRef}
        pageText={pageText}
        onPageTextChange={handlePageTextChange}
        totalPagesToDisplay={pagesToDisplay.length}
        fitToWidth={fitToWidth}
        onFitToWidth={() => onFitToWidth(scrollableRef)}
        onZoomOut={() => onZoomOut(scrollableRef)}
        onZoomIn={() => onZoomIn(scrollableRef)}
        zoomOutDisabled={zoomOutDisabled}
        zoomInDisabled={zoomInDisabled}
        onDownload={onDownload}
        onExpandContent={onExpandContent}
        onMinimizeContent={onMinimizeContent}
        onRotateLeft={handleRotateLeft}
        onRotateRight={handleRotateRight}
        onOpenInNewTab={onOpenInNewTab}
        onInteraction={onInteraction}
        scale={dynamicScale}
      />
    </div>
  )
}
